import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker, InputNumber, Card, Upload, message, Button, Col, Row, Spin, Typography, Select } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useAuthOrg } from '../context/AuthOrgContext';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT, CTW_NODE_API_ENDPOINT } from '../utils';
import axios from '../apiServices/monitoredAxios';
import { UploadOutlined, LinkOutlined } from '@ant-design/icons';
import { useMetaSetup } from '../context/MetaSetupContext';

const { Title, Text } = Typography;

interface AudienceInfo {
    audienceName: string;
    audienceFilters: string;
    audienceCategories: string[];
    estimatedSize: number;
    estimatedCpm: number;
}

interface Brand {
    brandDbId: number;
    brandId: number;
    brandName: string;
    website: string;
    logoUrl: string;
    orgId: string;
    orgName: string;
    isVerified: boolean;
    createdAt: string;
    about?: string;
}


const VerismartCampaignCreation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { currentOrg } = useAuthOrg();
    const { selectedAdAccount, adAccountList } = useMetaSetup();
    const audienceInfo = location.state as AudienceInfo;
    const [form] = Form.useForm();
    const [isMediaUploadedByFile, setIsMediaUploadedByFile] = useState(false);
    const [mediaUrl, setMediaUrl] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [audienceFilters, setAudienceFilters] = useState<string>('');
    const [audienceName, setAudienceName] = useState<string>('');
    const [audienceCategories, setAudienceCategories] = useState<string[]>([]);
    const [estimatedSize, setEstimatedSize] = useState<number>(0);
    const [estimatedCpm, setEstimatedCpm] = useState<number>(0);
    const [selectedBrandId, setSelectedBrandId] = useState<number>(0);


    useEffect(() => {
        setBreadcrumbs([
            {
                title: <Text style={{ cursor: 'pointer' }} onClick={() => navigate("/audience")}>Audience</Text>, // Updated to navigate to a more likely listing page
            },
            {
                title: <Text>Create Audience</Text>, // More specific title
            },
        ]);
    }, [setBreadcrumbs, navigate]);

    const [brands, setBrands] = useState<Brand[]>([]);

    useEffect(() => {
        if (audienceInfo) {
            setAudienceFilters(audienceInfo.audienceFilters);
            setAudienceName(audienceInfo.audienceName);
            setAudienceCategories(audienceInfo.audienceCategories);
            setEstimatedSize(audienceInfo.estimatedSize);
            setEstimatedCpm(audienceInfo.estimatedCpm);
        }
    }, [audienceInfo]);

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const response = await coreAPIService.get<{ meta: { status: boolean, code: number }, data: Brand[] }>(
                    CTW_API_ENDPOINT,
                    'v2/verismart/get-all-brands'
                );
                if (response.meta.code === 200 && response.data) {
                    setBrands(response.data);
                }
            } catch (error) {
                console.error('Error fetching brands:', error);
                message.error('Failed to fetch brands');
            }
        };
        fetchBrands();
    }, []);

    const handleSubmit = async (values: any) => {
        if (!mediaUrl) {
            message.error('Please upload a preview image or provide an image URL.');
            return;
        }
        // console.log(audienceInfo.audienceFilters);

        try {
            const search = window.location.search;
            const params = new URLSearchParams(search);

            const audienceId = params.get('audienceId');
            const { campaignName, budget, duration, url } = values; // Changed minBudget, maxBudget to budget
            const [startDate, endDate] = duration;
            const auth = localStorage.getItem("accessToken");
            setIsLoading(true);
            let audienceName = "";

            // Logic for deriving audienceName if audienceInfo is null
            if (audienceInfo === null && audienceFilters) {
                try {
                    const parsedFilters = JSON.parse(audienceFilters);
                    // Assuming the audience_group is consistently at a specific index or structure
                    const audienceGroupField = parsedFilters.find((field: any) => field.hasOwnProperty('audience_group'));
                    if (audienceGroupField) {
                        audienceName = audienceGroupField.audience_group.value;
                    } else {
                        // Fallback or default name if audience_group is not found
                        audienceName = "Default Audience";
                    }
                } catch (e) {
                    console.error("Error parsing filters for audience name:", e);
                    audienceName = "Default Audience"; // Fallback
                }
            }

            let parsedFilters: any = {};
            let formattedFilters: any = [];

            try {
                if (audienceFilters) {
                    parsedFilters = JSON.parse(audienceFilters);
                }
            } catch (e) {
                console.error("Error parsing audienceFilters:", e);
            }

            // Check if parsedFilters is an array or object
            if (Array.isArray(parsedFilters)) {
                // New format: Array of objects with nested value structure
                // Example: [{percentage: {value: 0}}, {audience_group: {value: "test"}}]

                const audienceGroupFilter = parsedFilters.find(filter => filter.audience_group);

                if (!audienceGroupFilter || !audienceGroupFilter.audience_group?.value) {
                    message.warning("Audience filters are missing. Cannot save.");
                }

                // Remove audience_group and percentage
                formattedFilters = parsedFilters.filter(
                    filter => !filter.audience_group && !filter.percentage
                );

                formattedFilters.forEach((filter: any) => {
                    filter.value = { ...filter.value, mandatory: true };
                });

            } else if (typeof parsedFilters === 'object' && parsedFilters !== null) {
                // Old format: Object with direct key-value pairs
                // Example: {"Annual Income": ["40L - 50L"], "spend_category": ["Electronics"]}

                // Convert object to array of objects with nested value structure
                formattedFilters = Object.entries(parsedFilters).map(([key, filterValue]: [string, any]) => {
                    return {
                        [key]: {
                            value: filterValue.value || filterValue, // Handle both {value: x} and direct value
                            mandatory: true
                        }
                    };
                });
            }


            const requestBody = {
                flag: "Create",
                createAudienceRequest: {
                    brand_id: selectedBrandId, // Consider making this dynamic or configurable if needed
                    mandatoryTrueCount: formattedFilters.length,
                    fields: [
                        {
                            percentage: {
                                value: 0 // Default percentage
                            }
                        },
                        {
                            audience_group: {
                                value: audienceInfo?.audienceName || values.campaignName
                            }
                        },
                        // Safely parse and spread filters
                        // Safely parse and spread filters
                        ...formattedFilters,
                        // ...(audienceFilters && Array.isArray(JSON.parse(audienceFilters)) ? JSON.parse(audienceFilters) : [])
                    ]
                },
                createVerismartCampaignData: {
                    preview_image: mediaUrl,
                    previewImage: mediaUrl,
                    query_type: "and",
                    fields: [
                        { campaign_name: { value: campaignName.trim() } },
                        { campaign_type: { value: "BRANDING" } },
                        { about: { value: "" } },
                        { destination_url: { value: url.trim() } },
                        { start_duration: { value: startDate.toISOString() } },
                        { end_duration: { value: endDate.toISOString() } },
                        { volume: { value: 1000 } },
                        { min_budget: { value: budget } },
                        { max_budget: { value: budget } },
                        { monthly_active_user: { value: null } },
                        { category: { value: audienceCategories || [] } },
                        { conversion_type: { value: "CPM" } },
                        { payout_percentage: { value: "" } },
                        { payout_terms: { value: "" } },
                        { trial_period: { value: "" } },
                        { pref_campaign_channel: { value: [3] } },
                        { whatsapp_template: { value: "" } },
                        { whatsapp_agent: { value: "" } },
                        { meta_delivery_type: { value: "MANUAL" } },
                        { ad_manager_id: { value: values?.adAccountId?.split("_")[1] || selectedAdAccount?.id?.split("_")[1] || "" } }
                    ],
                }
            };

            if (audienceInfo) {
                Object.assign(requestBody.createAudienceRequest, { estimated_cost: String(audienceInfo.estimatedCpm) })
                Object.assign(requestBody.createAudienceRequest, { estimated_size: String(audienceInfo.estimatedSize) })
            }

            const response = await axios.post(
                CTW_API_ENDPOINT + `/v2/verismart/create-Audience-and-campaign${audienceId ? `?id=${audienceId}` : ""}`, // Ensure this endpoint is correct
                requestBody,
                {
                    headers: {
                        'Authorization': "Bearer " + auth, // Ensure auth token is valid and prefixed if needed (e.g., 'Bearer ')
                        'Content-Type': 'application/json'
                    }
                }
            );

            setIsLoading(false);

            if ((response as any).data.meta.status === true && (response as any).data.meta.code === 200) {
                message.success('Campaign created successfully!');
                localStorage.removeItem('formattedFilters'); // Clear filters after successful creation
                navigate('/audience'); // Navigate to audience listing or relevant page
            } else {
                message.error((response as any).data.meta.message || 'Failed to create audience. Please check details.');
            }
        } catch (error: any) {
            setIsLoading(false);
            console.error('Error creating campaign:', error);
            if (error.response && error.response.data && error.response.data.meta) {
                message.error(error.response.data.meta.message || 'An unexpected error occurred.');
            } else {
                message.error('An unexpected error occurred while creating the campaign.');
            }
        }
    };

    const uploadMedia = async (file: File) => {
        if (!currentOrg?.orgId) {
            message.error("Account not found. Please log in again.");
            throw new Error("Account not found");
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', file.type);

        try {
            const response = await coreAPIService.post<{ data: { success: boolean; data: string; message?: string } }>(
                CTW_NODE_API_ENDPOINT,
                `/management/v1/save/${currentOrg.orgId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success && response.data.data) {
                return response.data.data;
            } else {
                message.error(response.data.message || "Media upload failed.");
                return "";
            }
        } catch (error) {
            console.error('Upload error:', error);
            message.error("An error occurred during media upload.");
            throw error;
        }
    };

    return (
        <div className="px-6 py-4 md:px-10">
            <Card bordered={false} className="shadow-xl rounded-lg p-4">
                <Title level={3} style={{ textAlign: 'left', marginBottom: '24px' }}>Create Custom Audience<br /><p className='text-xl text-gray-300'>Below details are required to be reviewed by the data owners.</p></Title>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    className="space-y-6"
                >
                    <Form.Item
                        label={<Text strong>Campaign Description</Text>}
                        name="campaignName"
                        rules={[{ required: true, message: 'Please enter the campaign name' }]}
                    >
                        <Input size="large" placeholder="e.g., Summer Sale Campaign" />
                    </Form.Item>
                    <Form.Item
                        label={<Text strong>Brand</Text>}
                        name="brand"
                        rules={[{ required: true, message: 'Please choose brand' }]}
                    >
                        <Select
                            size="large"
                            placeholder="Select a brand"
                            options={brands.map((brand) => ({ value: brand.brandId, label: brand.brandName }))}
                            onChange={(value) => setSelectedBrandId(value)}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<Text strong>Meta Ad Account</Text>}
                        name="adAccountId"
                        rules={[{ required: true, message: 'Please select an ad account' }]}
                        initialValue={selectedAdAccount?.id}
                    >
                        <Select
                            size="large"
                            placeholder="Select Meta Ad Account"
                            options={adAccountList.map((acc) => ({ value: acc.id, label: acc.name }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<Text strong>Campaign Destination Url</Text>}
                        name="url"
                        rules={[{ required: true, message: 'Please enter your site url.' }]}
                    >
                        <Input size="large" placeholder="e.g., https://..." />
                    </Form.Item>

                    <Form.Item
                        label={<Text strong>Preview Image of your campaign</Text>}
                        required // Marking the entire section as required visually
                    // No 'name' prop here as it's handled by mediaUrl state
                    >
                        <Row gutter={[16, 16]} align={"middle"}>
                            <Col xs={24} sm={10}>
                                <Upload
                                    maxCount={1}
                                    accept="image/*,.gif,.jpeg,.png,.jpg"
                                    beforeUpload={async (file) => {
                                        if (!file.type.startsWith('image/')) {
                                            message.error('Only image files (JPEG, PNG, GIF) are allowed.');
                                            return Upload.LIST_IGNORE;
                                        }
                                        // Max file size (e.g., 5MB)
                                        if (file.size / 1024 / 1024 > 5) {
                                            message.error('Image must be smaller than 5MB!');
                                            return Upload.LIST_IGNORE;
                                        }
                                        try {
                                            setIsLoading(true); // Show loading during upload
                                            const url:any = await uploadMedia(file);
                                            
                                            setIsLoading(false);
                                            if (!url.imageUrl) throw new Error('Upload returned no URL');
                                            setMediaUrl(url.imageUrl);
                                            setFileName(file.name);
                                            setIsMediaUploadedByFile(true);
                                            message.success(`${file.name} uploaded successfully.`);
                                            return false; // Prevent default antd upload
                                        } catch (error) {
                                            setIsLoading(false);
                                            message.error('Failed to upload media. Please try again.');
                                            return Upload.LIST_IGNORE; // Or return false if you want to keep the file in list on error
                                        }
                                    }}
                                    onRemove={() => {
                                        setMediaUrl('');
                                        setFileName(null);
                                        setIsMediaUploadedByFile(false);
                                        return true;
                                    }}
                                    disabled={isLoading || (!!mediaUrl && !isMediaUploadedByFile)} // Disable if loading or URL is manually entered
                                    listType="picture"
                                >
                                    <Button icon={<UploadOutlined />} size="large" disabled={isLoading || (!!mediaUrl && !isMediaUploadedByFile)}>
                                        {fileName || "Upload Image"}
                                    </Button>
                                </Upload>
                            </Col>
                            <Col xs={24} sm={4} style={{ textAlign: 'center' }}>
                                <Text type="secondary" strong>OR</Text>
                            </Col>
                            <Col xs={24} sm={10}>
                                <Input
                                    addonBefore={<LinkOutlined />}
                                    size="large"
                                    placeholder="Enter direct image URL (e.g., https://...)"
                                    value={mediaUrl}
                                    onChange={(e) => {
                                        setMediaUrl(e.target.value);
                                        setIsMediaUploadedByFile(false); // If URL is typed, it's not from file upload
                                        setFileName(null);
                                    }}
                                    disabled={isLoading || isMediaUploadedByFile} // Disable if loading or file is uploaded
                                />
                            </Col>
                        </Row>
                    </Form.Item>
                    <div className='flex flex-row w-full justify-between gap-8'>
                        <Form.Item
                            label={<Text strong>Duration</Text>}
                            name="duration"
                            rules={[{ required: true, message: 'Please select the campaign duration' }]}
                            className='w-1/2'
                        >
                            <DatePicker.RangePicker size="large" style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item
                            label={<Text strong>Budget</Text>} // Single budget field
                            name="budget" // Changed name
                            rules={[{ required: true, message: 'Please enter the campaign budget' }]}
                            className='w-1/2'

                        >
                            <InputNumber
                                prefix="₹"
                                size="large"
                                style={{ width: '100%' }}
                                placeholder="Enter total campaign budget"
                                min={0} // Minimum budget can be 0 or more
                            />
                        </Form.Item>

                    </div>

                    <Form.Item style={{ width: "200px", margin: "auto", marginTop: '32px' }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isLoading}
                            block
                            size="large"
                            style={{ marginTop: '32px' }}
                            icon={<Spin spinning={isLoading} size="small" style={{ marginRight: isLoading ? 8 : 0, color: 'white' }} />}
                        >
                            {isLoading ? 'Creating...' : 'Create Audience'}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default VerismartCampaignCreation;
